import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DollarSign, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, RefreshCw, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { usePersistentTabState } from '@/hooks/usePersistentTabState';

interface VendorFinanceDetailsProps {
  vendorId: string;
  vendorName: string;
}

interface VendorBalance {
  available_amount_cents: number;
  pending_amount_cents: number;
  total_earned_cents: number;
  total_withdrawn_cents: number;
  currency: string;
  updated_at: string;
}

interface VendorTransaction {
  id: string;
  type: 'credit' | 'debit' | 'withdrawal' | 'refund' | 'adjustment';
  net_amount_cents: number;
  commission_amount_cents: number | null;
  gross_amount_cents: number;
  source: string;
  order_id: string | null;
  description: string | null;
  created_at: string;
}

interface WithdrawalRequest {
  id: string;
  amount_cents: number;
  payment_method: string;
  payment_details: any;
  status: 'pending' | 'processing' | 'approved' | 'rejected' | 'completed';
  rejection_reason: string | null;
  transaction_reference: string | null;
  created_at: string;
  updated_at: string;
}

export default function VendorFinanceDetails({ vendorId, vendorName }: VendorFinanceDetailsProps) {
  const [balance, setBalance] = useState<VendorBalance | null>(null);
  const [transactions, setTransactions] = useState<VendorTransaction[]>([]);
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = usePersistentTabState(
    'admin-vendor-finance-details:activeTab',
    'transactions',
    ['transactions', 'withdrawals']
  );

  useEffect(() => {
    fetchVendorFinances();
  }, [vendorId]);

  const fetchVendorFinances = async () => {
    try {
      setLoading(true);

      // Fetch balance
      const { data: balanceData, error: balanceError } = await supabase
        .from('vendor_balances')
        .select('*')
        .eq('vendor_id', vendorId)
        .single();

      if (balanceError && balanceError.code !== 'PGRST116') {
        console.error('Error fetching balance:', balanceError);
      } else {
        setBalance(balanceData);
      }

      // Fetch transactions
      const { data: transactionsData, error: transactionsError } = await supabase
        .from('vendor_transactions')
        .select('*')
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (transactionsError) {
        console.error('Error fetching transactions:', transactionsError);
      } else {
        setTransactions(transactionsData || []);
      }

      // Fetch withdrawal requests
      const { data: withdrawalsData, error: withdrawalsError } = await supabase
        .from('vendor_withdrawal_requests')
        .select('*')
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false });

      if (withdrawalsError) {
        console.error('Error fetching withdrawals:', withdrawalsError);
      } else {
        setWithdrawals(withdrawalsData || []);
      }
    } catch (error) {
      console.error('Error fetching vendor finances:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (cents: number, currency: string = 'CDF') => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'credit':
        return <ArrowDownRight className="h-4 w-4 text-green-500" />;
      case 'withdrawal':
        return <ArrowUpRight className="h-4 w-4 text-red-500" />;
      case 'refund':
        return <TrendingDown className="h-4 w-4 text-orange-500" />;
      default:
        return <DollarSign className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTransactionBadge = (type: string) => {
    switch (type) {
      case 'credit':
        return <Badge variant="default" className="bg-green-500">Crédit</Badge>;
      case 'withdrawal':
        return <Badge variant="destructive">Retrait</Badge>;
      case 'refund':
        return <Badge variant="secondary">Remboursement</Badge>;
      case 'debit':
        return <Badge variant="outline">Débit</Badge>;
      default:
        return <Badge>{type}</Badge>;
    }
  };

  const getWithdrawalStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />En attente</Badge>;
      case 'processing':
        return <Badge variant="default">En cours</Badge>;
      case 'approved':
      case 'completed':
        return <Badge variant="default" className="bg-green-500">Approuvé</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejeté</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Balance Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Disponible</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {balance ? formatAmount(balance.available_amount_cents, balance.currency) : '0 CDF'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>En attente</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {balance ? formatAmount(balance.pending_amount_cents, balance.currency) : '0 CDF'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total gagné</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {balance ? formatAmount(balance.total_earned_cents, balance.currency) : '0 CDF'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total retiré</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-600">
              {balance ? formatAmount(balance.total_withdrawn_cents, balance.currency) : '0 CDF'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Transactions and Withdrawals */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="transactions">
            <TrendingUp className="h-4 w-4 mr-2" />
            Transactions ({transactions.length})
          </TabsTrigger>
          <TabsTrigger value="withdrawals">
            <DollarSign className="h-4 w-4 mr-2" />
            Retraits ({withdrawals.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="transactions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Historique des transactions</span>
                <Button variant="outline" size="sm" onClick={fetchVendorFinances}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Aucune transaction</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Montant brut</TableHead>
                      <TableHead className="text-right">Commission</TableHead>
                      <TableHead className="text-right">Montant net</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell className="text-sm">
                          {formatDistanceToNow(new Date(transaction.created_at), {
                            addSuffix: true,
                            locale: fr,
                          })}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            {getTransactionIcon(transaction.type)}
                            {getTransactionBadge(transaction.type)}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {transaction.description || transaction.source}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatAmount(transaction.gross_amount_cents, balance?.currency)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground">
                          {transaction.commission_amount_cents
                            ? `- ${formatAmount(transaction.commission_amount_cents, balance?.currency)}`
                            : '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold">
                          {transaction.type === 'withdrawal' || transaction.type === 'debit' ? '-' : '+'}
                          {formatAmount(Math.abs(transaction.net_amount_cents), balance?.currency)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="withdrawals" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Demandes de retrait</span>
                <Button variant="outline" size="sm" onClick={fetchVendorFinances}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {withdrawals.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Aucune demande de retrait</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Montant</TableHead>
                      <TableHead>Méthode</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Détails</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {withdrawals.map((withdrawal) => (
                      <TableRow key={withdrawal.id}>
                        <TableCell className="text-sm">
                          {formatDistanceToNow(new Date(withdrawal.created_at), {
                            addSuffix: true,
                            locale: fr,
                          })}
                        </TableCell>
                        <TableCell className="font-mono font-semibold">
                          {formatAmount(withdrawal.amount_cents, balance?.currency)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{withdrawal.payment_method}</Badge>
                        </TableCell>
                        <TableCell>{getWithdrawalStatusBadge(withdrawal.status)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {withdrawal.status === 'rejected' && withdrawal.rejection_reason ? (
                            <span className="text-red-600">{withdrawal.rejection_reason}</span>
                          ) : withdrawal.transaction_reference ? (
                            <span>Réf: {withdrawal.transaction_reference}</span>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
